/**
 * screens/run/matchRun.tsx
 * Match run / funnel — the engine centerpiece. Runs (or loads) a match against
 * /api/hire/v2, then renders the 4-stage funnel, rerank banner, Top matches
 * (score ≥ cutoff) with Shortlist/Submit/Reject, and the gated-out panel.
 */
import React, { useCallback, useEffect, useState } from 'react';
import { View, Text, ScrollView, Pressable, Platform, StyleSheet } from 'react-native';
import { useNavigation, useRoute } from '@react-navigation/native';
import type { RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';

import { useTheme } from '../../context/themeContext';
import { useBreakpoints, ContentMaxWidth } from '../../theme/responsive';
import { web } from '../../utils/web';
import { skillsOnlyBreakdown } from '../../utils/hireFormat';
import { useHover } from '../../hooks/useHover';
import { ActionButton, StatusChip, Panel, SegmentedBar, Icon } from '../../components/ui';
import Chip from '../../components/chip';
import Popover from '../../components/popover';
import { MiniAvatar } from '../../components/hire/badges';
import ReasonModal from '../../components/hire/reasonModal';
import { runMatch, getRun, getLatestRun, recordAction, listMyFeedback, markGoodMatch, markFalsePositive, clearFeedback, exclusionLookup } from '../../services/hire/matchV2';
import { Alert } from '../../components/alert';
import type {
  MatchRunDetail, TopMatch, RerankStatus, ScoreBreakdown, ActionType,
  ConfidenceLevel, ConfidenceReason, FeedbackVerdict, ExclusionResult,
} from '../../schemas/hire/matchV2';
import type { AppStackParamList } from '../../navigation/types';
import { toneColors, type Tone } from '../../theme/tone';

type Nav = NativeStackNavigationProp<AppStackParamList>;
type Rt = RouteProp<AppStackParamList, 'run'>;

/**
 * What the engine does, in the recruiter's words. The internals (SQL gates →
 * exact cosine → deterministic score → AI rerank) are engine trivia: the person
 * reading this screen wants to know the run progressed, not how it's built. Each
 * step maps 1:1 to a pipeline stage so nothing is invented.
 */
/**
 * The four stages, named for what they actually do.
 *
 * Stage 4 used to be called "Final Ranking · Strongest matches ordered first",
 * which was wrong twice over: the ranking is Stage 3's output and ALWAYS happens,
 * and Stage 4 is the optional AI note pass (it stopped setting the order when the
 * note cache replaced the rerank). So a completed run said "Final Ranking — not
 * needed" while sitting on a fully ranked list, which reads as the ranking having
 * been skipped.
 */
const STEPS = [
  { key: 'stage1', label: 'Eligibility Check', note: 'Must-have skills, experience and location' },
  { key: 'stage2', label: 'Skill Matching',    note: 'Every resume weighed against the role' },
  { key: 'stage3', label: 'Candidate Scoring', note: 'Scored out of 100 and ordered, best first' },
  { key: 'stage4', label: 'AI Fit Notes',      note: 'A short written note on each top match' },
] as const;

/**
 * What to show under Stage 4 when no notes were written.
 *
 * "not needed" was the old catch-all, and it was misleading in every case: the
 * commonest reason is that the workspace has no AI credits, which is a thing the
 * recruiter can act on — and the least likely reading of "not needed" is "you are
 * out of credits".
 */
function stage4Note(status: RerankStatus, count: number): string {
  if (count > 0) return `${count} noted`;
  switch (status) {
    case 'skipped_no_credits': return 'skipped — no AI credits';
    case 'skipped_incremental': return 'skipped — incremental run';
    case 'fallback': return 'unavailable — order unchanged';
    default: return 'no notes this run';
  }
}

const isWebPlatform = Platform.OS === 'web';

/** A soft premium card shadow — subtle depth without breaking the flat B&W system. */
const PANEL_SHADOW = web({ boxShadow: '0 1px 2px rgba(16,24,40,0.05), 0 14px 28px -20px rgba(16,24,40,0.16)' });

/** Premium staggered entrance (web-only; inert on native). */
function rise(delay: number) {
  return web({ animation: 'sk-rise 480ms cubic-bezier(.2,.7,.2,1) both', animationDelay: `${delay}ms` });
}

/** Animate a whole number 0 → target on mount, eased (web only; instant on native). */
function useCountUp(target: number, duration = 760): number {
  const [n, setN] = useState(isWebPlatform ? 0 : target);
  useEffect(() => {
    if (!isWebPlatform) { setN(target); return; }
    let raf = 0;
    const start = performance.now();
    const tick = (t: number) => {
      const p = Math.min(1, (t - start) / duration);
      const eased = 1 - Math.pow(1 - p, 3);
      setN(Math.round(target * eased));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, duration]);
  return n;
}

/** Shimmering placeholder block for the progressive/skeleton load state. */
function Skeleton({ w, h, r = 6, style }: { w?: number | string; h: number; r?: number; style?: object }) {
  const { theme } = useTheme();
  const { colors } = theme;
  return (
    <View
      style={[
        { width: (w as number) ?? '100%', height: h, borderRadius: r, backgroundColor: colors.paper2 },
        web({
          backgroundImage: `linear-gradient(90deg, ${colors.paper2} 0%, ${colors.paper3} 50%, ${colors.paper2} 100%)`,
          backgroundSize: '300% 100%',
          animation: 'sk-shimmer 1.4s ease-in-out infinite',
        }),
        style,
      ]}
    />
  );
}

function rerankBanner(status: RerankStatus, credits: number): { tone: Tone; text: string } {
  switch (status) {
    case 'ok': return { tone: 'success', text: `Reranked top 15 on summary_compressed only · ${credits} credit${credits === 1 ? '' : 's'} debited` };
    case 'fallback': return { tone: 'warn', text: 'Invalid JSON / timeout → fell back to Stage-3 order; debit voided' };
    case 'skipped_no_credits': return { tone: 'danger', text: 'Stage 4 skipped — balance below cap; Stage-3 order stands' };
    case 'skipped_incremental': return { tone: 'neutral', text: 'Incremental run — only the new candidates were scored; Stage 4 skipped by design, nothing charged' };
    // Server-controlled value: never let an unrecognised status crash the screen.
    default: return { tone: 'neutral', text: `Rerank status: ${String(status)}` };
  }
}

/**
 * A checkmark that is *written* rather than popped in: the path animates its
 * stroke-dashoffset so the tick draws itself as the step lands. Web renders raw
 * inline SVG (the project already avoids react-native-svg); native falls back to
 * the static glyph.
 */
function DrawnCheck({ size, color, delay }: { size: number; color: string; delay: number }) {
  if (!isWebPlatform) return <Icon name="check" size={size} color={color} />;
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true" style={{ display: 'block' }}>
      <path
        d="M5 12.5l4.6 4.6L19 7"
        stroke={color}
        strokeWidth={2.6}
        strokeLinecap="round"
        strokeLinejoin="round"
        style={{
          strokeDasharray: 30,
          animation: `sk-draw 460ms cubic-bezier(.65,0,.35,1) both`,
          animationDelay: `${delay}ms`,
        }}
      />
    </svg>
  );
}

/**
 * One step in the "Matching candidates" card. Three states:
 *   done    → filled badge whose tick draws itself, expanding halo, lit rail
 *   active  → orbiting ring + breathing core, travelling light on the rail
 *   pending → hollow badge, dimmed label
 * The rail lights downward after each tick, so the card reads as progress moving
 * through the pipeline rather than four unrelated rows appearing at once.
 */
type StepState = 'done' | 'active' | 'pending';

const BADGE = 34;

function StepRow({
  label, note, count, state, index, delay, last,
}: { label: string; note: string; count?: string | null; state: StepState; index: number; delay: number; last: boolean }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { hovered, hoverProps } = useHover();
  const done = state === 'done';
  const active = state === 'active';
  const lit = done || active;

  return (
    <View
      {...hoverProps}
      style={[
        {
          flexDirection: 'row', gap: 15, borderRadius: radii.lg, paddingVertical: 11, paddingHorizontal: 12,
          borderWidth: 1, borderColor: active ? colors.lineStrong : 'transparent',
          backgroundColor: active ? colors.paper2 : hovered ? colors.paper2 : 'transparent',
          overflow: 'hidden',
        },
        web({ transition: 'background 200ms ease, border-color 200ms ease, transform 240ms cubic-bezier(.2,.7,.2,1)' }),
        hovered ? web({ transform: 'translateX(3px)' }) : undefined,
        isWebPlatform ? web({ animation: 'sk-unblur 640ms cubic-bezier(.2,.7,.2,1) both', animationDelay: `${delay}ms` }) : undefined,
      ]}
    >
      {/* one-shot light sweep across the row as the step completes */}
      {isWebPlatform && done && (
        <View
          pointerEvents="none"
          style={web({
            position: 'absolute', top: 0, bottom: 0, left: 0, width: '45%',
            backgroundImage: `linear-gradient(90deg, transparent, ${colors.ink}0F, transparent)`,
            animation: 'sk-row-sweep 1100ms ease-out both',
            animationDelay: `${delay + 240}ms`,
          })}
        />
      )}

      {/* badge + rail column */}
      <View style={{ width: BADGE, alignItems: 'center' }}>
        <View style={{ width: BADGE, height: BADGE, alignItems: 'center', justifyContent: 'center' }}>
          {/* expanding halo — fires once, as the tick draws */}
          {isWebPlatform && done && (
            <View
              pointerEvents="none"
              style={web({
                position: 'absolute', width: BADGE, height: BADGE, borderRadius: BADGE / 2,
                border: `1.5px solid ${colors.ink}`,
                animation: 'sk-ring-once 1000ms cubic-bezier(.2,.7,.2,1) both',
                animationDelay: `${delay + 180}ms`,
              })}
            />
          )}
          {/* orbiting sweep ring — only while this step is the one running */}
          {isWebPlatform && active && (
            <View
              pointerEvents="none"
              style={web({
                position: 'absolute', width: BADGE + 8, height: BADGE + 8, borderRadius: (BADGE + 8) / 2,
                backgroundImage: `conic-gradient(from 0deg, transparent 0deg, transparent 250deg, ${colors.ink3} 340deg, transparent 360deg)`,
                animation: 'sk-orbit 1.5s linear infinite',
                WebkitMask: 'radial-gradient(circle, transparent 58%, #000 60%)',
                mask: 'radial-gradient(circle, transparent 58%, #000 60%)',
              })}
            />
          )}
          <View
            style={[
              {
                width: BADGE, height: BADGE, borderRadius: BADGE / 2, alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                backgroundColor: done ? colors.ink : colors.paper2,
                borderWidth: done ? 0 : 1,
                borderColor: active ? colors.ink3 : colors.line,
              },
              // Depth: a soft top highlight on the filled badge + a cast shadow,
              // so the completed marker sits above the card rather than on it.
              done ? web({
                backgroundImage: `linear-gradient(160deg, ${colors.ink2}, ${colors.ink})`,
                boxShadow: `0 6px 14px -6px ${colors.ink}66, inset 0 1px 0 rgba(255,255,255,0.18)`,
              }) : undefined,
              done && isWebPlatform ? web({ animation: 'sk-badge-in 620ms cubic-bezier(.2,.9,.25,1.05) both', animationDelay: `${delay}ms` }) : undefined,
              hovered && done ? web({ transform: 'scale(1.08)' }) : undefined,
              web({ transition: 'transform 220ms cubic-bezier(.2,.7,.2,1), box-shadow 220ms ease' }),
            ]}
          >
            {isWebPlatform && done && (
              <View
                pointerEvents="none"
                style={web({
                  position: 'absolute', top: 0, bottom: 0, width: '65%',
                  backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                  animation: 'sk-gleam 5.5s ease-in-out infinite',
                  animationDelay: `${1200 + delay}ms`,
                })}
              />
            )}
            {done ? (
              <DrawnCheck size={17} color={colors.onAccent} delay={delay + 170} />
            ) : active ? (
              <View style={[{ width: 9, height: 9, borderRadius: 5, backgroundColor: colors.ink2 }, isWebPlatform ? web({ animation: 'sk-breathe 1.25s ease-in-out infinite' }) : undefined]} />
            ) : (
              <Text style={{ fontFamily: font.mono, fontSize: 10, fontWeight: fontWeight.bold, color: colors.ink4 }}>{index + 1}</Text>
            )}
          </View>
        </View>

        {/* rail to the next step: fills once this step is done, and carries a
            travelling light while the next step is the one running */}
        {!last && (
          <View style={{ flex: 1, minHeight: 22, width: 2, marginVertical: 5, borderRadius: 1, backgroundColor: colors.line, overflow: 'hidden' }}>
            <View
              style={[
                { flex: 1 },
                done ? web({ backgroundImage: `linear-gradient(180deg, ${colors.ink2}, ${colors.ink4})` }) : undefined,
                done && isWebPlatform
                  ? web({ transformOrigin: 'top', animation: 'sk-rail 560ms cubic-bezier(.2,.7,.2,1) both', animationDelay: `${delay + 260}ms` })
                  : undefined,
              ]}
            />
            {isWebPlatform && done && (
              <View
                pointerEvents="none"
                style={web({
                  position: 'absolute', left: 0, right: 0, height: '55%',
                  backgroundImage: `linear-gradient(180deg, transparent, ${colors.ink}, transparent)`,
                  animation: 'sk-rail-pulse 1200ms ease-in-out both',
                  animationDelay: `${delay + 420}ms`,
                })}
              />
            )}
          </View>
        )}
      </View>

      {/* label + state caption */}
      <View style={{ flex: 1, minWidth: 0, paddingTop: 4, paddingBottom: last ? 2 : 12 }}>
        <Text
          numberOfLines={1}
          style={[
            { fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: lit ? colors.ink : colors.ink4, letterSpacing: -0.2 },
            web({ transition: 'color 240ms ease' }),
          ]}
        >
          {label}
        </Text>
        <Text numberOfLines={2} style={{ fontSize: fontSize.xs, color: colors.ink4, marginTop: 2, lineHeight: fontSize.xs * 1.45 }}>{note}</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 5 }}>
          {active && (
            <View style={[{ width: 5, height: 5, borderRadius: 3, backgroundColor: colors.ink2 }, isWebPlatform ? web({ animation: 'sk-breathe 1.25s ease-in-out infinite' }) : undefined]} />
          )}
          <Text
            style={[
              { fontFamily: font.mono, fontSize: 10, letterSpacing: 0.7, textTransform: 'uppercase', fontWeight: fontWeight.semibold, color: done ? colors.ink3 : active ? colors.ink2 : colors.ink4 },
              active && isWebPlatform ? web({ animation: 'sk-breathe 1.5s ease-in-out infinite' }) : undefined,
            ]}
          >
            {done ? (count ?? 'Completed') : active ? 'In progress' : 'Pending'}
          </Text>
        </View>
      </View>
    </View>
  );
}

/**
 * Desktop step tile — the same three states laid out as a wide, connected
 * stepper. Vertical rows left a whole column of dead space on a 1440px screen;
 * across the card the four steps read as one pipeline, which is what they are.
 */
function StepTile({
  label, note, count, state, index, delay, first, last,
}: { label: string; note: string; count?: string | null; state: StepState; index: number; delay: number; first: boolean; last: boolean }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font, radii } = theme;
  const { hovered, hoverProps } = useHover();
  const done = state === 'done';
  const active = state === 'active';
  const lit = done || active;

  // A connector half-segment. Left halves belong to the step they lead into, so
  // a segment lights only once the step behind it has landed.
  const rail = (side: 'l' | 'r', on: boolean) => (
    <View style={{ flex: 1, height: 2, borderRadius: 1, backgroundColor: colors.line, overflow: 'hidden' }}>
      <View
        style={[
          { flex: 1 },
          on ? web({ backgroundImage: `linear-gradient(90deg, ${colors.ink4}, ${colors.ink2})` }) : undefined,
          on && isWebPlatform
            ? web({ transformOrigin: side === 'l' ? 'left' : 'left', animation: 'sk-rail-x 480ms cubic-bezier(.2,.7,.2,1) both', animationDelay: `${delay + (side === 'l' ? 40 : 220)}ms` })
            : undefined,
        ]}
      />
      {isWebPlatform && on && (
        <View
          pointerEvents="none"
          style={web({
            position: 'absolute', top: 0, bottom: 0, width: '55%',
            backgroundImage: `linear-gradient(90deg, transparent, ${colors.ink}, transparent)`,
            animation: 'sk-rail-pulse-x 1100ms ease-in-out both',
            animationDelay: `${delay + 340}ms`,
          })}
        />
      )}
    </View>
  );

  return (
    <View
      {...hoverProps}
      style={[
        {
          flex: 1, minWidth: 0, borderRadius: radii.lg, paddingTop: 14, paddingBottom: 16, paddingHorizontal: 4,
          borderWidth: 1, borderColor: active ? colors.lineStrong : 'transparent',
          backgroundColor: active || hovered ? colors.paper2 : 'transparent',
          overflow: 'hidden', gap: 12,
        },
        web({ transition: 'background 220ms ease, border-color 220ms ease, transform 260ms cubic-bezier(.2,.7,.2,1), box-shadow 260ms ease' }),
        hovered ? web({ transform: 'translateY(-3px)', boxShadow: '0 14px 26px -20px rgba(16,24,40,0.35)' }) : undefined,
        isWebPlatform ? web({ animation: 'sk-unblur 680ms cubic-bezier(.2,.7,.2,1) both', animationDelay: `${delay}ms` }) : undefined,
      ]}
    >
      {/* ambient pool of light under the step that is currently running */}
      {isWebPlatform && active && (
        <View
          pointerEvents="none"
          style={web({
            position: 'absolute', top: -18, left: '50%', marginLeft: -90, width: 180, height: 110,
            backgroundImage: `radial-gradient(50% 50% at 50% 50%, ${colors.ink}1A, transparent 70%)`,
            animation: 'sk-spotlight 2.4s ease-in-out infinite',
          })}
        />
      )}

      {/* light sweep across the tile as it completes */}
      {isWebPlatform && done && (
        <View
          pointerEvents="none"
          style={web({
            position: 'absolute', top: 0, bottom: 0, left: 0, width: '50%',
            backgroundImage: `linear-gradient(90deg, transparent, ${colors.ink}0F, transparent)`,
            animation: 'sk-row-sweep 1200ms ease-out both',
            animationDelay: `${delay + 260}ms`,
          })}
        />
      )}

      {/* badge on the connector line */}
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 0 }}>
        {first ? <View style={{ flex: 1 }} /> : rail('l', done || active)}
        <View style={{ width: BADGE + 12, alignItems: 'center', justifyContent: 'center', height: BADGE }}>
          {isWebPlatform && done && (
            <View
              pointerEvents="none"
              style={web({
                position: 'absolute', width: BADGE, height: BADGE, borderRadius: BADGE / 2,
                border: `1.5px solid ${colors.ink}`,
                animation: 'sk-ring-once 1000ms cubic-bezier(.2,.7,.2,1) both',
                animationDelay: `${delay + 180}ms`,
              })}
            />
          )}
          {isWebPlatform && active && (
            <View
              pointerEvents="none"
              style={web({
                position: 'absolute', width: BADGE + 9, height: BADGE + 9, borderRadius: (BADGE + 9) / 2,
                backgroundImage: `conic-gradient(from 0deg, transparent 0deg, transparent 250deg, ${colors.ink3} 340deg, transparent 360deg)`,
                animation: 'sk-orbit 1.5s linear infinite',
                WebkitMask: 'radial-gradient(circle, transparent 58%, #000 60%)',
                mask: 'radial-gradient(circle, transparent 58%, #000 60%)',
              })}
            />
          )}
          <View
            style={[
              {
                width: BADGE, height: BADGE, borderRadius: BADGE / 2, alignItems: 'center', justifyContent: 'center', overflow: 'hidden',
                backgroundColor: done ? colors.ink : colors.paper,
                borderWidth: done ? 0 : 1,
                borderColor: active ? colors.ink3 : colors.line,
              },
              done ? web({
                backgroundImage: `linear-gradient(160deg, ${colors.ink2}, ${colors.ink})`,
                boxShadow: `0 6px 14px -6px ${colors.ink}66, inset 0 1px 0 rgba(255,255,255,0.18)`,
              }) : undefined,
              done && isWebPlatform ? web({ animation: 'sk-badge-in 620ms cubic-bezier(.2,.9,.25,1.05) both', animationDelay: `${delay}ms` }) : undefined,
              hovered && done ? web({ transform: 'scale(1.1)' }) : undefined,
              web({ transition: 'transform 240ms cubic-bezier(.2,.7,.2,1), box-shadow 240ms ease' }),
            ]}
          >
            {isWebPlatform && done && (
              <View
                pointerEvents="none"
                style={web({
                  position: 'absolute', top: 0, bottom: 0, width: '65%',
                  backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.5), transparent)',
                  animation: 'sk-gleam 5.5s ease-in-out infinite',
                  animationDelay: `${1200 + delay}ms`,
                })}
              />
            )}
            {done ? (
              <DrawnCheck size={17} color={colors.onAccent} delay={delay + 170} />
            ) : active ? (
              <View style={[{ width: 9, height: 9, borderRadius: 5, backgroundColor: colors.ink2 }, isWebPlatform ? web({ animation: 'sk-breathe 1.25s ease-in-out infinite' }) : undefined]} />
            ) : (
              <Text style={{ fontFamily: font.mono, fontSize: 10, fontWeight: fontWeight.bold, color: colors.ink4 }}>{index + 1}</Text>
            )}
          </View>
        </View>
        {last ? <View style={{ flex: 1 }} /> : rail('r', done)}
      </View>

      {/* label block */}
      <View style={{ alignItems: 'center', gap: 4, paddingHorizontal: 8 }}>
        <Text numberOfLines={1} style={[{ fontSize: fontSize.md, fontWeight: fontWeight.semibold, color: lit ? colors.ink : colors.ink4, letterSpacing: -0.2 }, web({ transition: 'color 240ms ease' })]}>
          {label}
        </Text>
        <Text numberOfLines={2} style={{ fontSize: fontSize.xs, color: colors.ink4, textAlign: 'center', lineHeight: fontSize.xs * 1.5 }}>{note}</Text>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6, marginTop: 3 }}>
          {active && <View style={[{ width: 5, height: 5, borderRadius: 3, backgroundColor: colors.ink2 }, isWebPlatform ? web({ animation: 'sk-breathe 1.25s ease-in-out infinite' }) : undefined]} />}
          <Text
            style={[
              { fontFamily: font.mono, fontSize: 10, letterSpacing: 0.7, textTransform: 'uppercase', fontWeight: fontWeight.semibold, color: done ? colors.ink3 : active ? colors.ink2 : colors.ink4 },
              active && isWebPlatform ? web({ animation: 'sk-breathe 1.5s ease-in-out infinite' }) : undefined,
            ]}
          >
            {done ? (count ?? 'Completed') : active ? 'In progress' : 'Pending'}
          </Text>
        </View>
      </View>
    </View>
  );
}

/**
 * The four plain-English steps of a match run, with a progress meter above them.
 * `activeIndex` drives the in-flight view (steps before it are done, that one is
 * running); pass `null` for a finished run so every step reads Completed.
 */
function MatchSteps({ activeIndex, counts }: { activeIndex: number | null; counts?: (string | null)[] }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  const { isMobile, isTablet } = useBreakpoints();
  // Four tiles across need real width; below that they'd squeeze the labels, so
  // narrow viewports keep the stacked rows.
  const stacked = isMobile || isTablet;
  const total = STEPS.length;
  const doneCount = activeIndex === null ? total : activeIndex;
  const complete = doneCount === total;
  const pct = useCountUp(Math.round((doneCount / total) * 100), 900);
  const stateOf = (i: number): StepState =>
    activeIndex === null || i < activeIndex ? 'done' : i === activeIndex ? 'active' : 'pending';

  return (
    <View style={{ gap: 14 }}>
      {/* Header: a medallion that states the outcome, and the share of steps done
          as a large count-up figure — the card leads with its conclusion. */}
      <View style={{ gap: 12 }}>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12 }}>
          <View style={{ width: 38, height: 38, alignItems: 'center', justifyContent: 'center' }}>
            {/* slow halo behind the medallion */}
            {isWebPlatform && (
              <View
                pointerEvents="none"
                style={web({
                  position: 'absolute', width: 38, height: 38, borderRadius: 19,
                  border: `1px solid ${colors.line}`,
                  animation: complete ? 'sk-halo 3.6s ease-out infinite' : 'sk-halo 2.2s ease-out infinite',
                })}
              />
            )}
            <View
              style={[
                { width: 38, height: 38, borderRadius: 12, alignItems: 'center', justifyContent: 'center', backgroundColor: colors.paper2, borderWidth: 1, borderColor: colors.line, overflow: 'hidden' },
                isWebPlatform ? web({ animation: 'sk-unblur 620ms cubic-bezier(.2,.7,.2,1) both' }) : undefined,
              ]}
            >
              <View style={!complete && isWebPlatform ? web({ animation: 'sk-breathe 1.5s ease-in-out infinite' }) : undefined}>
                <Icon name={complete ? 'check-circle' : 'sparkles'} size={19} color={colors.ink2} />
              </View>
            </View>
          </View>
          <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
            <Text numberOfLines={1} style={{ fontSize: fontSize.md, fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.3 }}>
              {complete ? 'Matching complete' : 'Matching in progress'}
            </Text>
            <Text style={{ fontFamily: font.mono, fontSize: 10, letterSpacing: 0.7, textTransform: 'uppercase', fontWeight: fontWeight.semibold, color: colors.ink4 }}>
              {doneCount} of {total} steps
            </Text>
          </View>
          {/* the figure counts up as the meter fills, so the two read as one move */}
          <Text style={{ fontFamily: font.mono, fontSize: 22, fontWeight: fontWeight.bold, color: complete ? colors.ink : colors.ink3, letterSpacing: -0.8 }}>
            {pct}<Text style={{ fontSize: 13, color: colors.ink4 }}>%</Text>
          </Text>
        </View>
        <View style={{ height: 4, borderRadius: 999, backgroundColor: colors.paper3, overflow: 'hidden' }}>
          <View
            style={[
              { height: 4, borderRadius: 999, width: `${(doneCount / total) * 100}%`, backgroundColor: colors.ink },
              web({
                backgroundImage: `linear-gradient(90deg, ${colors.ink3}, ${colors.ink})`,
                transition: 'width 780ms cubic-bezier(.2,.7,.2,1)',
              }),
            ]}
          >
            {isWebPlatform && (
              <View
                pointerEvents="none"
                style={web({
                  position: 'absolute', top: 0, bottom: 0, width: '40%',
                  backgroundImage: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.35), transparent)',
                  animation: 'sk-sheen 1400ms ease-out both',
                  animationDelay: `${total * 150 + 300}ms`,
                })}
              />
            )}
          </View>
        </View>
      </View>

      {stacked ? (
        <View style={{ gap: 2 }}>
          {STEPS.map((s, i) => (
            <StepRow
              key={s.key}
              label={s.label}
              note={s.note}
              count={counts?.[i] ?? null}
              index={i}
              state={stateOf(i)}
              delay={i * 170}
              last={i === STEPS.length - 1}
            />
          ))}
        </View>
      ) : (
        <View style={{ flexDirection: 'row', alignItems: 'flex-start' }}>
          {STEPS.map((s, i) => (
            <StepTile
              key={s.key}
              label={s.label}
              note={s.note}
              count={counts?.[i] ?? null}
              index={i}
              state={stateOf(i)}
              delay={i * 170}
              first={i === 0}
              last={i === STEPS.length - 1}
            />
          ))}
        </View>
      )}
    </View>
  );
}

function segments(b: ScoreBreakdown) {
  return [
    { value: b.niceToHave, tone: 'purple' as Tone },
    { value: b.experience, tone: 'info' as Tone },
    { value: b.title, tone: 'teal' as Tone },
    { value: b.tenure, tone: 'warn' as Tone },
    { value: b.recency, tone: 'neutral' as Tone },
  ];
}

// Confidence → tone (grayscale-mapped: HIGH/MEDIUM/LOW read as distinct
// lightness, per the strict-grayscale design system). Outline, not fill —
// metadata about the score, never competing with the Strong Match badge.
const CONF_TONE: Record<ConfidenceLevel, Tone> = { high: 'success', medium: 'warn', low: 'danger' };

function ConfidenceChip({ level, reasons }: { level: ConfidenceLevel; reasons: ConfidenceReason[] | null }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const tc = toneColors(colors, CONF_TONE[level]);
  const chip = (
    <Pressable
      style={[{ flexDirection: 'row', alignItems: 'center', height: 20, paddingHorizontal: 8, borderRadius: radii.full, borderWidth: 1, borderColor: tc.fg, backgroundColor: 'transparent' }, web({ cursor: 'help' })]}
    >
      <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.semibold, color: tc.fg, letterSpacing: 0.4 }}>{level.toUpperCase()}</Text>
    </Pressable>
  );
  if (!reasons || reasons.length === 0) return chip;
  return (
    <Popover trigger={chip} width={244} align="left">
      <View style={{ padding: 6, gap: 6 }}>
        <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.bold, color: colors.ink3 }}>Extraction confidence</Text>
        {reasons.map((r, i) => (
          <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
            <Text style={{ fontSize: fontSize.sm, color: r.ok ? colors.success : colors.danger }}>{r.ok ? '✓' : '✗'}</Text>
            <Text style={{ fontSize: fontSize.xs, color: colors.ink2 }}>{r.label}</Text>
          </View>
        ))}
      </View>
    </Popover>
  );
}

/** Analytics-only feedback — deliberately quiet; subordinate to pipeline actions.
 *  One verdict per candidate; tapping the active verdict undoes it. */
function FeedbackControls({ verdict, onVote }: { verdict: FeedbackVerdict | null; onVote: (v: FeedbackVerdict) => void }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, radii } = theme;
  const Btn = ({ v, label }: { v: FeedbackVerdict; label: string }) => {
    const on = verdict === v;
    return (
      <Pressable
        onPress={() => onVote(v)}
        style={[{ flexDirection: 'row', alignItems: 'center', gap: 4, height: 22, paddingHorizontal: 9, borderRadius: radii.full, borderWidth: 1, borderColor: on ? colors.ink3 : colors.line, backgroundColor: on ? colors.paper3 : 'transparent' }, web({ cursor: 'pointer', transition: 'all 120ms ease' })]}
      >
        {on && <Text style={{ fontSize: fontSize.xs, color: colors.ink }}>✓</Text>}
        <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.medium, color: on ? colors.ink : colors.ink4 }}>{label}</Text>
      </Pressable>
    );
  };
  return (
    <View style={{ flexDirection: 'row', gap: 6, justifyContent: 'flex-end' }}>
      <Btn v="good_match" label="Good match" />
      <Btn v="false_positive" label="False positive" />
    </View>
  );
}

/** A Top-matches row with hover highlight + entrance. Responsive: a dense
 *  horizontal row on wide screens, a stacked card on compact / mobile so the
 *  columns never crush together. */
function MatchRow({
  m, rank, first, done, stacked, verdict, onVote, onShortlist, onSubmit, onReject,
}: {
  m: TopMatch; rank: number; first: boolean; done?: string; stacked: boolean;
  verdict: FeedbackVerdict | null; onVote: (v: FeedbackVerdict) => void;
  onShortlist: () => void; onSubmit: () => void; onReject: () => void;
}) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  const { hovered, hoverProps } = useHover();
  const score = useCountUp(m.score);
  const entrance = rise(60 + rank * 55);

  const scoreValue = (
    <Text style={{ fontFamily: font.mono, fontSize: fontSize.md, fontWeight: fontWeight.bold, color: colors.ink, fontVariant: ['tabular-nums'] }}>
      {score}<Text style={{ fontSize: fontSize.xs, color: colors.ink3, fontWeight: fontWeight.medium }}>/100</Text>
    </Text>
  );
  const badges = (
    <>
      {m.isStrongMatch && <View style={web({ animation: 'sk-pop 420ms cubic-bezier(.2,.8,.2,1) both', animationDelay: `${120 + rank * 55}ms` })}><Chip tone="success" dot={false}>Strong Match</Chip></View>}
      {m.extractionConfidence && <ConfidenceChip level={m.extractionConfidence} reasons={m.confidenceReasons} />}
    </>
  );
  const scoreBar = (
    <>
      <SegmentedBar segments={segments(m.breakdown)} max={100} grow delay={100 + rank * 55} />
      <Text numberOfLines={1} style={{ fontFamily: font.mono, fontSize: 10, color: colors.ink4, marginTop: 5 }}>{skillsOnlyBreakdown(m.breakdownString)}</Text>
      {m.extractionConfidence === 'low' && (
        <Text style={{ fontSize: 10, color: colors.warn, marginTop: 3 }}>Parsing incomplete — review resume.</Text>
      )}
    </>
  );
  const rationale = m.rationale ? (
    <Text numberOfLines={stacked ? 4 : 3} style={{ fontSize: fontSize.xs, color: colors.purple, fontStyle: 'italic', lineHeight: 16 }}>
      {`“${m.rationale}”`}
    </Text>
  ) : null;
  const actions = done ? (
    <StatusChip tone="success" label={`✓ ${done.replace(/_/g, ' ')}`} dot={false} />
  ) : stacked ? (
    <View style={{ flexDirection: 'row', gap: 8 }}>
      <View style={{ flex: 1 }}><ActionButton label="Shortlist" variant="secondary" size="sm" icon="star" onPress={onShortlist} /></View>
      <View style={{ flex: 1 }}><ActionButton label="Submit" variant="primary" size="sm" icon="send" onPress={onSubmit} /></View>
      <View style={{ flex: 1 }}><ActionButton label="Reject" variant="danger" size="sm" icon="x" onPress={onReject} /></View>
    </View>
  ) : (
    <View style={{ flexDirection: 'row', gap: 7, flexWrap: 'nowrap', justifyContent: 'flex-end' }}>
      <ActionButton label="Shortlist" variant="secondary" size="sm" icon="star" onPress={onShortlist} />
      <ActionButton label="Submit" variant="primary" size="sm" icon="send" onPress={onSubmit} />
      <ActionButton label="Reject" variant="danger" size="sm" icon="x" onPress={onReject} />
    </View>
  );

  /* ── Stacked card (mobile / compact) ─────────────────────────────────── */
  if (stacked) {
    return (
      <View
        {...hoverProps}
        style={[
          { gap: 12, paddingHorizontal: 16, paddingVertical: 16, borderTopWidth: first ? 0 : 1, borderTopColor: colors.line, backgroundColor: hovered ? colors.paper2 : 'transparent' },
          web({ transition: 'background 160ms ease' }),
          entrance,
        ]}
      >
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10 }}>
          <Text style={{ width: 20, fontFamily: font.mono, fontSize: fontSize.sm, fontWeight: fontWeight.bold, color: colors.ink3 }}>{rank}</Text>
          <MiniAvatar name={m.name} size={34} />
          <View style={{ flex: 1, minWidth: 0 }}>
            <Text numberOfLines={1} style={{ fontSize: fontSize.md, color: colors.ink, fontWeight: fontWeight.semibold }}>{m.name}</Text>
            <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink4, marginTop: 1 }}>cosine {m.cosine != null ? m.cosine.toFixed(3) : '—'}</Text>
          </View>
          {scoreValue}
        </View>
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>{badges}</View>
        <View>{scoreBar}</View>
        {rationale}
        <View style={{ height: 1, backgroundColor: colors.line }} />
        {actions}
        <FeedbackControls verdict={verdict} onVote={onVote} />
      </View>
    );
  }

  /* ── Dense horizontal row (wide) ─────────────────────────────────────── */
  return (
    <View
      {...hoverProps}
      style={[
        { flexDirection: 'row', alignItems: 'center', gap: 16, paddingHorizontal: 18, paddingVertical: 16, borderTopWidth: first ? 0 : 1, borderTopColor: colors.line, backgroundColor: hovered ? colors.paper2 : 'transparent' },
        web({ transition: 'background 160ms ease, transform 160ms cubic-bezier(.2,.8,.2,1)' }),
        hovered && web({ transform: 'translateX(2px)' }),
        entrance,
      ]}
    >
      {/* hover accent rail — a quiet lightness cue on the active row */}
      <View style={[{ position: 'absolute', left: 0, top: 6, bottom: 6, width: 3, borderRadius: 3, backgroundColor: colors.ink, opacity: hovered ? 0.9 : 0 }, web({ transition: 'opacity 160ms ease' })]} />
      <Text style={{ width: 22, fontFamily: font.mono, fontSize: fontSize.md, fontWeight: fontWeight.bold, color: colors.ink3 }}>{rank}</Text>
      <View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, flex: 1.6, minWidth: 0 }}>
        <MiniAvatar name={m.name} size={30} />
        <Text numberOfLines={1} style={{ fontSize: fontSize.md, color: colors.ink, fontWeight: fontWeight.semibold }}>{m.name}</Text>
      </View>
      <Text style={{ width: 60, fontFamily: font.mono, fontSize: fontSize.sm, color: colors.ink2 }}>{m.cosine != null ? m.cosine.toFixed(3) : '—'}</Text>
      <View style={{ flex: 2, minWidth: 0 }}>
        {/* Score row: independent signals — score, Strong Match badge,
            confidence chip — visually distinct, none competing. */}
        <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          {scoreValue}
          {badges}
        </View>
        <View style={{ marginTop: 6 }}>{scoreBar}</View>
      </View>
      <View style={{ flex: 1.5, minWidth: 0 }}>
        {/* AI fit note (Stage 4). When rerank fell back silently, no slot renders. */}
        {rationale}
      </View>
      <View style={{ flex: 2.4, minWidth: 0, alignItems: 'flex-end', gap: 8 }}>
        {actions}
        <FeedbackControls verdict={verdict} onVote={onVote} />
      </View>
    </View>
  );
}

/** Gated-out row — tapping the name opens the candidate's profile; the trailing
 *  "Why not?" opens the first-failing gate (declared vs actual). Neutral info
 *  styling — exclusion is a fact, not an error. */
function GatedRow({
  requisitionId, candidateId, name, reason, detailLine, delay,
}: { requisitionId: string; candidateId: string; name: string; reason: string; detailLine: string | null; delay: number }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight } = theme;
  const navigation = useNavigation<Nav>();
  const { hovered, hoverProps } = useHover();
  const [detail, setDetail] = useState<ExclusionResult | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchDetail = () => {
    if (detail || loading) return;
    setLoading(true);
    void exclusionLookup(requisitionId, candidateId)
      .then(setDetail)
      .catch(() => setDetail(null))
      .finally(() => setLoading(false));
  };

  const whyNot = (
    <Pressable style={[{ flexDirection: 'row', alignItems: 'center', gap: 6, flexShrink: 0 }, web({ cursor: 'pointer' })]}>
      <Text style={{ fontSize: fontSize.xs, color: colors.ink4 }}>{reason}</Text>
      <Text style={{ fontSize: fontSize.xs, color: colors.info, fontWeight: fontWeight.medium }}>Why not?</Text>
    </Pressable>
  );

  return (
    <View
      {...hoverProps}
      style={[
        { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', gap: 12, paddingVertical: 9, paddingHorizontal: 10, borderRadius: 8, backgroundColor: hovered ? colors.paper2 : 'transparent' },
        web({ transition: 'background 130ms ease' }),
        rise(delay),
      ]}
    >
      <Pressable
        onPress={() => navigation.navigate('candidateDetail', { id: candidateId })}
        style={[{ flexDirection: 'row', alignItems: 'center', gap: 9, flex: 1, minWidth: 0 }, web({ cursor: 'pointer' })]}
      >
        <MiniAvatar name={name} size={22} />
        {/* The specific reason sits UNDER the name, because that is the question
            the recruiter actually has: not "which gate" but "what about them".
            The category stays on the right; "Why not?" still opens the full
            declared-vs-actual breakdown. */}
        <View style={{ flex: 1, minWidth: 0, gap: 2 }}>
          <Text numberOfLines={1} style={{ fontSize: fontSize.sm, color: colors.ink2 }}>{name}</Text>
          {detailLine ? (
            <Text numberOfLines={2} style={{ fontSize: fontSize.xs, color: colors.ink3, lineHeight: 15 }}>
              {detailLine}
            </Text>
          ) : null}
        </View>
      </Pressable>
      <Popover trigger={whyNot} width={288} align="right" style={{ flexShrink: 0 }}>
      <View style={{ padding: 8, gap: 8 }} onLayout={fetchDetail}>
        <Text style={{ fontSize: fontSize.xs, fontWeight: fontWeight.bold, color: colors.ink3 }}>Why not matching?</Text>
        {loading || !detail ? (
          <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>Checking…</Text>
        ) : detail.passes === false && 'declared' in detail ? (
          <>
            <View style={{ borderLeftWidth: 3, borderLeftColor: colors.info, paddingLeft: 10, gap: 3 }}>
              <Text style={{ fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: colors.ink }}>{detail.label}</Text>
              <Text style={{ fontSize: fontSize.xs, color: colors.ink2 }}>Required — {detail.declared}</Text>
              <Text style={{ fontSize: fontSize.xs, color: colors.ink2 }}>Candidate — {detail.actual}</Text>
            </View>
            <Text style={{ fontSize: 10, color: colors.ink4 }}>Checked against Stage-1 requirements in order.</Text>
          </>
        ) : detail.eligible === false ? (
          <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>Not eligible for matching.</Text>
        ) : (
          <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>Passes all Stage-1 gates.</Text>
        )}
        </View>
      </Popover>
    </View>
  );
}

/** In-flight view. Instead of a blank centered spinner (which reads as a hang),
 *  we paint the real page scaffold immediately and let the four pipeline stages
 *  breathe while the engine runs — the screen feels alive from the first frame. */
function RunningView({ isMobile }: { isMobile: boolean }) {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  // Walk the highlighted step forward while the engine works, holding on the
  // last one until the real result replaces this view. The server doesn't stream
  // per-stage progress, so this is a paced indicator, not a claim of position —
  // it never marks the final step complete.
  const [active, setActive] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setActive((i) => Math.min(STEPS.length - 1, i + 1)), 1500);
    return () => clearInterval(t);
  }, []);
  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 18 : 32, paddingBottom: 72 }}>
      <View style={{ width: '100%', maxWidth: ContentMaxWidth.default, alignSelf: 'center', gap: 20 }}>
        <View style={[{ flexDirection: 'row', alignItems: 'center', gap: 12 }, rise(0)]}>
          <View style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: colors.accent, alignItems: 'center', justifyContent: 'center', ...PANEL_SHADOW }}>
            <View style={isWebPlatform ? web({ animation: 'sk-breathe 1.5s ease-in-out infinite' }) : undefined}>
              <Icon name="sparkles" size={22} color={colors.onAccent} />
            </View>
          </View>
          <View style={{ gap: 6 }}>
            <Text style={{ fontSize: isMobile ? 22 : 26, fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.6 }}>Running match</Text>
            <Text style={{ fontSize: fontSize.sm, color: colors.ink3 }}>Checking eligibility, matching skills and ranking the pool</Text>
          </View>
        </View>

        <View style={rise(110)}>
          <Panel title="Matching candidates" style={PANEL_SHADOW}>
            <MatchSteps activeIndex={active} />
          </Panel>
        </View>

        <View style={rise(200)}>
          <Panel title="Ranked matches" flush style={PANEL_SHADOW}>
            {[0, 1, 2].map((i) => (
              <View key={i} style={{ flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingVertical: 16, borderTopWidth: i === 0 ? 0 : 1, borderTopColor: colors.line }}>
                <Skeleton w={30} h={30} r={999} />
                <View style={{ flex: 1.6, gap: 6 }}><Skeleton w={'62%'} h={13} /><Skeleton w={'40%'} h={10} /></View>
                <View style={{ flex: 2, gap: 7 }}><Skeleton w={'80%'} h={12} /><Skeleton w={'100%'} h={6} r={999} /></View>
                <View style={{ flex: 2.4, alignItems: 'flex-end' }}><Skeleton w={200} h={30} r={8} /></View>
              </View>
            ))}
          </Panel>
        </View>

        <Text style={[{ textAlign: 'center', fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink4 }, isWebPlatform ? web({ animation: 'sk-breathe 1.5s ease-in-out infinite' }) : undefined]}>
          crunching candidates…
        </Text>
      </View>
    </ScrollView>
  );
}

export default function MatchRunScreen() {
  const { theme } = useTheme();
  const { colors, fontSize, fontWeight, font } = theme;
  const { isMobile, isWide } = useBreakpoints();
  const navigation = useNavigation<Nav>();
  const { params } = useRoute<Rt>();

  const [run, setRun] = useState<MatchRunDetail | null>(null);
  const [running, setRunning] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [acted, setActed] = useState<Record<string, string>>({});
  const [feedback, setFeedback] = useState<Record<string, FeedbackVerdict>>({});
  const [reasonFor, setReasonFor] = useState<{ candidateId: string; action: ActionType } | null>(null);

  /**
   * ARRIVING here shows the LAST run's stored results — it never fires a new one.
   * A match run costs AI rerank tokens, so re-scoring an unchanged job just to
   * reproduce the same ranking is pure waste.
   *
   * Two exceptions run without being asked twice:
   *
   *   • No run at all — there is nothing to show otherwise.
   *   • `autoRun` (the caller pressed "Run match") AND the stored run is stale.
   *     Pressing "Run match" and being shown a ranking that predates the
   *     candidate you just ingested reads as the run having missed them; the
   *     button said it would run, so it runs. A newly ingested candidate only
   *     appeared after separately pressing "Score N", which is the same work
   *     one click later.
   *
   * Which KIND of run follows from what actually went stale: a job edit moved the
   * criteria, so every stored score is invalid and it takes a full run; anything
   * else is candidates, which an incremental run covers — it scores only the ones
   * the last run never judged, leaves the rest byte-for-byte, and skips Stage 4.
   * So the common "I added a candidate" case costs no rerank tokens.
   *
   * A fresh stored run still short-circuits: nothing changed, so there is nothing
   * to spend on, and the ranking on screen is already the answer.
   */
  const start = useCallback(async () => {
    setRunning(true); setError(null);
    try {
      if (params.runId) {
        setRun(await getRun(params.runId));
        return;
      }
      const latest = await getLatestRun(params.requisitionId);
      if (!latest.run) {
        setRun(await runMatch(params.requisitionId));            // first ever run
        return;
      }
      const st = latest.staleness;
      if (params.autoRun && !st.fresh) {
        setRun(await runMatch(params.requisitionId, st.jobChanged ? undefined : 'incremental'));
        return;
      }
      setRun(latest.run);                                        // stored, nothing charged
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(msg || 'Could not load the match.');
    } finally {
      setRunning(false);
    }
  }, [params.requisitionId, params.runId, params.autoRun]);

  useEffect(() => { void start(); }, [start]);

  // Hydrate this recruiter's own feedback verdicts for the current run.
  useEffect(() => {
    if (!run) return;
    let cancelled = false;
    void (async () => {
      try {
        const items = await listMyFeedback(run.id);
        if (!cancelled) setFeedback(Object.fromEntries(items.map((i) => [i.candidateId, i.verdict])));
      } catch { /* feedback is non-essential; ignore load failures */ }
    })();
    return () => { cancelled = true; };
  }, [run?.id]);

  // Re-run always POSTs a brand-new match (never getRun), even when we're
  // already viewing a fresh run — navigating to the same route wouldn't retrigger.
  const reRun = useCallback(async (requisitionId: string, incremental = false) => {
    setRunning(true); setError(null); setActed({}); setFeedback({});
    try {
      setRun(await runMatch(requisitionId, incremental ? 'incremental' : undefined));
    } catch (e) {
      const msg = (e as { response?: { data?: { error?: { message?: string } } } })?.response?.data?.error?.message;
      setError(msg || 'Could not run the match.');
    } finally {
      setRunning(false);
    }
  }, []);

  const act = async (m: TopMatch, action: ActionType, notes?: string) => {
    if (!run) return;
    await recordAction({ requisitionId: run.requisitionId, candidateId: m.candidateId, matchRunId: run.id, action, notes });
    setActed((prev) => ({ ...prev, [m.candidateId]: action }));
  };

  // Analytics-only feedback capture — never changes this ranking. Tapping the
  // active verdict undoes it; switching replaces.
  const vote = async (candidateId: string, v: FeedbackVerdict) => {
    if (!run) return;
    const current = feedback[candidateId] ?? null;
    try {
      if (current === v) {
        await clearFeedback(run.id, candidateId);
        setFeedback((p) => { const n = { ...p }; delete n[candidateId]; return n; });
      } else {
        if (v === 'good_match') await markGoodMatch(run.id, candidateId);
        else await markFalsePositive(run.id, candidateId);
        setFeedback((p) => ({ ...p, [candidateId]: v }));
      }
    } catch { /* non-fatal — feedback is best-effort */ }
  };

  if (running) return <RunningView isMobile={isMobile} />;
  if (error || !run) {
    return (
      <View style={styles.center}>
        <Text style={{ fontSize: fontSize.md, color: colors.ink }}>{error || 'No run.'}</Text>
        <View style={{ marginTop: 12 }}><ActionButton label="Retry" variant="secondary" onPress={() => void start()} /></View>
      </View>
    );
  }

  const banner = rerankBanner(run.rerankStatus, run.creditsCharged);
  // How many earned the badge — the cutoff is a badge threshold, not a filter.
  const strongCount = run.topMatches.filter((m) => m.isStrongMatch).length;

  return (
    <ScrollView style={{ flex: 1 }} contentContainerStyle={{ padding: isMobile ? 18 : 32, paddingBottom: 72 }}>
      <View style={{ width: '100%', maxWidth: ContentMaxWidth.default, alignSelf: 'center', gap: 20 }}>
        <Pressable onPress={() => navigation.navigate('jobDetail', { id: run.requisitionId })} style={[{ flexDirection: 'row', alignItems: 'center', gap: 5, alignSelf: 'flex-start' }, rise(0), web({ cursor: 'pointer' })]}>
          <Icon name="chev-l" size={15} color={colors.ink3} />
          <Text style={{ fontSize: fontSize.sm, color: colors.ink2, fontWeight: fontWeight.medium }}>Job</Text>
        </Pressable>

        <View style={[{ flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }, rise(50)]}>
          <View style={{ flexDirection: 'row', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
            <View style={{ width: 44, height: 44, borderRadius: 12, backgroundColor: colors.accent, alignItems: 'center', justifyContent: 'center', ...PANEL_SHADOW }}>
              <Icon name="sparkles" size={22} color={colors.onAccent} />
            </View>
            <View style={{ gap: 4, flex: 1, minWidth: 0 }}>
              <Text style={{ fontSize: isMobile ? 22 : 26, fontWeight: fontWeight.bold, color: colors.ink, letterSpacing: -0.6 }}>Match run</Text>
              <Text style={{ fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink4 }}>rev {run.revisionNo} · {new Date(run.ranAt).toLocaleString()}</Text>
            </View>
          </View>
          <ActionButton label="Re-run" variant="secondary" icon="refresh" onPress={() => void reRun(run.requisitionId)} />
        </View>

        {/* What the engine did, in plain steps — the stage-by-stage counts are
            engine internals and no longer shown here. */}
        <View style={rise(110)}>
          <Panel title="Matching candidates" style={PANEL_SHADOW}>
            {/* The status line under each step becomes its NUMBER once a run has
                finished. Without this the screen said "Completed" four times and
                never how many people were considered — so a job whose pool was 2
                looked identical to one whose pool was 200, and a single gated row
                read as a matching bug rather than an empty pool. */}
            <MatchSteps
              activeIndex={null}
              counts={[
                run.poolSize != null ? `${run.funnel.stage1} of ${run.poolSize} eligible` : `${run.funnel.stage1} eligible`,
                `${run.funnel.stage2} matched`,
                `${run.funnel.stage3} scored`,
                stage4Note(run.rerankStatus, run.funnel.stage4),
              ]}
            />
          </Panel>
        </View>

        {/* Rerank banner + exact AI token spend (sk-hire ledger; platform owns the limit).
            A full-width banner (not a pill) so the sentence wraps instead of
            overflowing the viewport on narrow screens. */}
        <View style={[{ gap: 10 }, rise(170)]}>
          {(() => {
            const bc = toneColors(colors, banner.tone);
            return (
              <View style={{ flexDirection: 'row', alignItems: 'flex-start', gap: 9, alignSelf: 'stretch', paddingVertical: 11, paddingHorizontal: 13, borderRadius: 10, backgroundColor: bc.bg, borderWidth: 1, borderColor: bc.border }}>
                <View style={{ width: 7, height: 7, borderRadius: 999, backgroundColor: bc.fg, marginTop: 5 }} />
                <Text style={{ flex: 1, minWidth: 0, fontSize: fontSize.sm, fontWeight: fontWeight.semibold, color: bc.fg, lineHeight: 19 }}>{banner.text}</Text>
              </View>
            );
          })()}
          {run.rerankStatus === 'ok' && run.rerankTotalTokens > 0 && (
            <View style={{ flexDirection: 'row', alignItems: 'center', gap: 7, alignSelf: 'flex-start', paddingVertical: 6, paddingHorizontal: 10, borderRadius: 8, borderWidth: 1, borderColor: colors.line, backgroundColor: colors.paper2 }}>
              <Icon name="zap" size={13} color={colors.ink3} />
              <Text style={{ flexShrink: 1, fontFamily: font.mono, fontSize: fontSize.xs, color: colors.ink2 }}>
                {run.rerankTotalTokens.toLocaleString()} tokens · {run.rerankInputTokens.toLocaleString()} in + {run.rerankOutputTokens.toLocaleString()} out{run.rerankModel ? ` · ${run.rerankModel}` : ''}
              </Text>
            </View>
          )}
        </View>

        {/* WI-5 — requisition skills the taxonomy didn't recognize (text match only). */}
        {run.unrecognizedSkills?.length > 0 && (
          <View style={[{ flexDirection: 'row', alignItems: 'center', gap: 8, flexWrap: 'wrap' }, rise(200)]}>
            <Icon name="info" size={13} color={colors.ink3} />
            <Text style={{ fontSize: fontSize.xs, color: colors.ink3 }}>
              Unrecognized skills (matched by text only): {run.unrecognizedSkills.join(', ')}
            </Text>
          </View>
        )}

        {/* Ranked matches — FULL list; the cutoff is a badge, never a filter. */}
        <View style={rise(220)}>
          {/* The title used to read "Ranked matches · 11 · Strong Match ≥ 60",
              which reads as a FILTER — "only 60+ is listed" — and then the list
              shows 59, 47, 38. It was never a filter: every ranked candidate is
              listed, and the cutoff only decides who earns the badge. Say that
              in words instead of leaving a threshold next to a count. */}
          <Panel title={`Ranked matches · ${run.rankedCount}`} flush style={PANEL_SHADOW}>
            <Text style={{ paddingHorizontal: 16, paddingTop: 10, fontSize: fontSize.xs, color: colors.ink4, lineHeight: 17 }}>
              {`All ${run.rankedCount} shown, best first`}
              {strongCount > 0
                ? ` — the ${strongCount} scoring ${run.cutoff} or more are badged Strong Match.`
                : ` — none reached ${run.cutoff}, the Strong Match mark.`}
              {'\n'}Feedback tunes future rule updates. It never changes this ranking.
            </Text>
            {run.topMatches.length === 0 ? (
              /**
               * Three different situations used to share one sentence, and only one
               * of them was actionable by "broaden the criteria":
               *   pool 0        — nobody is matchable at all (candidates exist but
               *                   have no extracted profile, or none were ingested)
               *   pool > 0      — real people were considered and every one was
               *                   excluded; the reasons are in the panel below
               *   pool unknown  — an older run, or an api that predates poolSize
               */
              <Text style={[styles.empty, { color: colors.ink3 }]}>
                {run.poolSize === 0
                  ? 'No candidates were available to match. Candidates only enter matching once their resume has been extracted — ingest a candidate, or check that extraction finished.'
                  : run.poolSize != null
                    ? `All ${run.poolSize} candidate${run.poolSize === 1 ? '' : 's'} considered were excluded — see “Not selected” below for the reason against each one.`
                    : 'No candidates cleared the Stage-1 gates. Broaden the criteria or ingest more candidates.'}
              </Text>
            ) : (
              run.topMatches.map((m, i) => (
                <MatchRow
                  key={m.candidateId}
                  m={m}
                  rank={i + 1}
                  first={i === 0}
                  stacked={!isWide}
                  done={acted[m.candidateId]}
                  verdict={feedback[m.candidateId] ?? null}
                  onVote={(v) => void vote(m.candidateId, v)}
                  onShortlist={() => void act(m, 'shortlisted')}
                  onSubmit={() => void act(m, 'submitted_to_client')}
                  onReject={() => setReasonFor({ candidateId: m.candidateId, action: 'rejected' })}
                />
              ))
            )}
          </Panel>
        </View>

        {/* Gated out */}
        {/* Rendered when the run recorded ANY exclusion, not only when one is
            renderable — otherwise a run whose every excluded candidate has since
            been deactivated shows no panel and no explanation, which is exactly
            the "it doesn't tell me why" complaint. */}
        {(run.gatedOut.length > 0 || (run.gatedTotal ?? 0) > 0) && (
          <View style={rise(280)}>
            <Panel
              title={
                run.gatedTotal > run.gatedOut.length
                  ? `Not selected · showing ${run.gatedOut.length} of ${run.gatedTotal}`
                  : `Not selected · ${run.gatedOut.length}`
              }
              style={PANEL_SHADOW}
            >
              <View style={{ gap: 2 }}>
                {/* Say why the list is short instead of leaving it to look broken. */}
                {run.gatedHidden > 0 && (
                  <Text style={{ fontSize: fontSize.xs, color: colors.ink4, paddingHorizontal: 10, paddingBottom: 6 }}>
                    {run.gatedHidden} more {run.gatedHidden === 1 ? 'candidate is' : 'candidates are'} not shown — deactivated or erased since this run.
                  </Text>
                )}
                {run.gatedOut.map((g, i) => (
                  <GatedRow
                    key={g.candidateId}
                    requisitionId={run.requisitionId}
                    candidateId={g.candidateId}
                    name={g.name}
                    reason={g.reasonLabel}
                    detailLine={g.reasonDetail}
                    delay={300 + i * 40}
                  />
                ))}
              </View>
            </Panel>
          </View>
        )}
      </View>

      <ReasonModal
        open={!!reasonFor}
        title="Reject candidate"
        onClose={() => setReasonFor(null)}
        onSubmit={async (reason) => {
          if (reasonFor) {
            const m = run.topMatches.find((x) => x.candidateId === reasonFor.candidateId);
            if (m) await act(m, reasonFor.action, reason);
          }
          setReasonFor(null);
        }}
      />
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  center: { flex: 1, alignItems: 'center', justifyContent: 'center', padding: 32 },
  empty: { padding: 20, textAlign: 'center', fontSize: 12, color: '#9E9E9E' },
});
